/**
 * ChatAttachment Component
 * 
 * A unified attachment display component for the GlobalChatSystem.
 * Handles both File objects and SerializedAttachment consistently
 * with a unified blue color scheme matching the application's design.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import type { SerializedAttachment, AttachmentVariant, ChatAttachmentProps } from './types';
import { formatFileSize, truncateFilename, getFileCategory, type FileCategory } from './utils';
import { docVersionFactFor } from './docVersionFact';
import { isStaleSignedUrl } from './attachmentRecords';
import { getUploadProgress } from '../../utils/attachmentUploadProgress';
import FilePreviewOverlay from '../FilePreview/FilePreviewOverlay';
import { downloadAttachmentAsFile, saveBlobAs } from '../FilePreview/downloadAttachment';
import {
  canResolveServerSide,
  resolveAttachmentContent,
  resolveAttachmentDisplayUrl,
  type AttachmentResolveContext,
} from '../FilePreview/resolveAttachment';
import { ImageHoverWrapper } from './components';
import { resolvePreviewKind } from '../FilePreview/previewKind';
import {
  InlineAttachmentImage,
  InlineAttachmentBrokenPlaceholder,
  InlineMediaActionButton,
  InlineMediaFrame,
  InlineMediaMetaRow,
  ImageLightboxBackdrop,
  ImageLightboxCloseButton,
} from './styles';

// ============================================================================
// Animations
// ============================================================================

export const attachmentSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ============================================================================
// Unified Blue Color Configuration
// ============================================================================

/**
 * Consistent BRAND color scheme for ALL attachments (2026-08-12 owner
 * directive: chat elements ride the Prest0 palette — warm ink + gold — the
 * old #2196f3 material blue was a one-off hue foreign to the platform).
 *
 * This ensures all attachments (user uploads, deep research reports,
 * formatted reports) have the same cohesive, on-brand appearance.
 */
const ATTACHMENT_COLORS = {
  border: 'rgba(198, 167, 94, 0.35)',
  borderHover: 'rgba(198, 167, 94, 0.55)',
  background: 'rgba(198, 167, 94, 0.10)',
  backgroundHover: 'rgba(198, 167, 94, 0.18)',
  iconColor: '#8A6D2F',
  accent: '#8A6D2F',
  // Section divider color
  divider: 'rgba(198, 167, 94, 0.15)',
  // Hover shadow with gold tint
  hoverShadow: '0 4px 12px rgba(198, 167, 94, 0.20)',
  // Gold tint (2026-08-20): was a leftover material-blue rgba(33,150,243,…)
  // from the pre-brand palette — the one non-brand hue in the scheme.
  activeShadow: '0 2px 6px rgba(198, 167, 94, 0.12)',
};

/**
 * USER-BUBBLE variant (owner directive 2026-08-20): the default palette is
 * literally the user bubble's own gold at low opacity, so a card on a USER
 * message rendered gold-on-gold — borders and tint invisible, ink unreadable.
 * On the gold bubble the card goes WHITE with the sanctioned warm ink
 * (#1C1917, SPECIAL_MESSAGE_COLORS.accent) — contrast by value, same scheme.
 * Applied through CSS custom properties (see AttachmentCard) so every
 * descendant (size, badges, icons, divider) follows without prop-threading.
 */
const USER_BUBBLE_ATTACHMENT_COLORS = {
  border: 'rgba(28, 25, 23, 0.22)',
  borderHover: 'rgba(28, 25, 23, 0.38)',
  background: 'rgba(255, 255, 255, 0.94)',
  backgroundHover: '#ffffff',
  iconColor: '#1C1917',
  accent: '#1C1917',
  divider: 'rgba(28, 25, 23, 0.22)',
  hoverShadow: '0 4px 12px rgba(28, 25, 23, 0.18)',
  activeShadow: '0 2px 6px rgba(28, 25, 23, 0.12)',
};

// ============================================================================
// Styled Components
// ============================================================================

/**
 * Narrow-width shrink priority (round 2, owner screenshot 2026-08-02): at
 * docked ~460px the round-1 contract ("name is the ONE shrink candidate")
 * ellipsized the FILENAME to NOTHING — the fixed-width Export/Download badge
 * + size + cloud glyph consumed the whole card and it read "130KB ☁
 * Export / Download PDF" with no name at all. The card is now its own CSS
 * container (`attachment-card`) and sheds trailing weight as ITS OWN width
 * narrows (container-driven, never viewport queries — the panel width
 * varies), in explicit priority order:
 *   1. NAME — always visible; hard floor ~10ch before ellipsis
 *      (MetaWrapper/AttachmentFileName min-width) — a card must never
 *      render nameless;
 *   2. ACTION — compacts to icon + short format label ("PDF"/"DOCX") at
 *      ≤ CARD_COMPACT_ACTION_MAX; the full "Export / Download …" wording
 *      survives as the badge's aria-label/title;
 *   3. SIZE — drops entirely at ≤ CARD_DROP_SIZE_MAX (name beats size);
 *   4. CLOUD GLYPH — last, drops at ≤ CARD_DROP_CLOUD_MAX (the card's
 *      aria-label/title still carry the stored-in-cloud hint).
 */
const CARD_COMPACT_ACTION_MAX = '400px';
const CARD_DROP_SIZE_MAX = '280px';
const CARD_DROP_CLOUD_MAX = '230px';

const AttachmentCard = styled.button<{
  $animate: boolean;
  $disabled: boolean;
  $onGold?: boolean;
}>`
  /* Palette as CSS custom properties (2026-08-20 user-bubble contrast fix):
     descendants (name, size, badges, cloud glyph) consume var(--att-*) so the
     ONE $onGold switch here recolors the whole card family. */
  --att-border: ${ATTACHMENT_COLORS.border};
  --att-border-hover: ${ATTACHMENT_COLORS.borderHover};
  --att-bg: ${ATTACHMENT_COLORS.background};
  --att-bg-hover: ${ATTACHMENT_COLORS.backgroundHover};
  --att-ink: ${ATTACHMENT_COLORS.iconColor};
  --att-accent: ${ATTACHMENT_COLORS.accent};
  --att-hover-shadow: ${ATTACHMENT_COLORS.hoverShadow};
  --att-active-shadow: ${ATTACHMENT_COLORS.activeShadow};
  ${props => props.$onGold && css`
    --att-border: ${USER_BUBBLE_ATTACHMENT_COLORS.border};
    --att-border-hover: ${USER_BUBBLE_ATTACHMENT_COLORS.borderHover};
    --att-bg: ${USER_BUBBLE_ATTACHMENT_COLORS.background};
    --att-bg-hover: ${USER_BUBBLE_ATTACHMENT_COLORS.backgroundHover};
    --att-ink: ${USER_BUBBLE_ATTACHMENT_COLORS.iconColor};
    --att-accent: ${USER_BUBBLE_ATTACHMENT_COLORS.accent};
    --att-hover-shadow: ${USER_BUBBLE_ATTACHMENT_COLORS.hoverShadow};
    --att-active-shadow: ${USER_BUBBLE_ATTACHMENT_COLORS.activeShadow};
  `}

  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.9rem;
  border-radius: 10px;
  background: var(--att-bg);
  border: 1px solid var(--att-border);
  color: #1e293b;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.65 : 1};
  transition: all 0.2s ease;
  text-align: left;
  /* Docked crush fix (owner feedback, Miles 2026-08-01): the card fills the
     full available message width in every mode (min-width: 0 lets it shrink
     WITH the bubble instead of overflowing it), keeps a sane single-row
     height, and never wraps its flex items — the name column is the ONE
     shrink candidate (see MetaWrapper/AttachmentFileName); icon, cloud
     glyph, and the Export/Download badge are all flex: none. */
  width: 100%;
  min-width: 0;
  min-height: 2.75rem;
  flex-wrap: nowrap;
  font-family: 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  /* Round 2 (2026-08-02): the card is a named container so the shrink
     cascade above keys off the CARD's real width (bubble-dependent), not the
     viewport and not only the pane. inline-size containment is safe here:
     width: 100% is explicit, block size stays content-driven. */
  container-type: inline-size;
  container-name: attachment-card;

  /* Narrow pane (docked included — same tier as the rest of the bottom
     region): tighten the inter-element gap a step so the name keeps more of
     the row. Keyed off the PANE container (a container query can't see its
     own element's width). */
  @container chat-pane (max-width: 480px) {
    gap: 0.5rem;
  }

  ${props => props.$animate && css`
    animation: ${attachmentSlideIn} 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  `}

  svg {
    color: var(--att-ink);
    stroke: var(--att-ink);
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    background: var(--att-bg-hover);
    border-color: var(--att-border-hover);
    transform: translateY(-1px);
    box-shadow: var(--att-hover-shadow);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: var(--att-active-shadow);
  }
`;

const IconWrapper = styled.div`
  width: 22px;
  height: 22px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * Docked crush fix (owner feedback, Miles 2026-08-01): the name column is
 * the card's ONE flexible region — flex: 1 1 auto so it absorbs narrow
 * widths by ELLIPSIZING, never by breaking the filename vertically one
 * character per line (the docked screenshot:
 * "d/e/e/p/-/r/e/s/e/a/r/c/h/…"). Name and size sit INLINE on a single
 * baseline row — the size never stacks under (or inside) the name.
 *
 * Round 2 (2026-08-02): the shrink floor. min-width went 0 → 10ch so flex
 * can never squeeze the name column out of existence (the ~460px docked
 * screenshot: cards rendered NAMELESS). overflow: hidden is the backstop
 * between container-query steps — if the size hasn't dropped yet, it clips
 * at the wrapper's edge instead of overlapping the action badge.
 */
const MetaWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 0.5rem;
  min-width: 10ch;
  flex: 1 1 auto;
  overflow: hidden;
`;

const AttachmentFileName = styled.span`
  font-weight: 600;
  font-size: 0.84rem;
  color: #1e293b;
  line-height: 1.3;
  /* Single-line ellipsis, never a mid-word/vertical break at any width.
     (Two truncation layers: the truncateFilename JS helper middle-truncates
     long names to ~35 chars preserving the extension; this CSS ellipsis is
     the responsive second layer for whatever width remains. The card's
     title tooltip carries the full name.) */
  /* Round 2 (2026-08-02): ~10ch visible floor before ellipsis — the name is
     priority 1 of the shrink cascade and a card must never render nameless. */
  min-width: 10ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
`;

const AttachmentFileSize = styled.span`
  font-size: 0.72rem;
  color: var(--att-accent, ${ATTACHMENT_COLORS.accent});
  font-weight: 500;
  opacity: 0.85;
  /* Inline beside the name, whole ("130KB"), never wrapped or squeezed. */
  flex: none;
  white-space: nowrap;

  /* Round 2 (2026-08-02): priority 3 — the size is the DROPPABLE element.
     At extreme card narrowness it disappears entirely (name beats size);
     the byte count stays discoverable through the preview overlay. */
  @container attachment-card (max-width: ${CARD_DROP_SIZE_MAX}) {
    display: none;
  }
`;

/* DOC-COMPLETENESS (2026-09-01): quiet version chip for generated documents.
   Renders ONLY when the server stamped docVersion ≥ 2 (an edit's new version
   of a living document) — absent identity = today's card, byte-identical.
   House voice: lowercase, calm; the chip states a fact, never shouts. */
const DocVersionChip = styled.span`
  font-size: 0.66rem;
  font-weight: 600;
  color: var(--att-accent, ${ATTACHMENT_COLORS.accent});
  border: 1px solid var(--att-border, ${ATTACHMENT_COLORS.border});
  border-radius: 999px;
  padding: 0.08rem 0.42rem;
  line-height: 1.3;
  flex: none;
  white-space: nowrap;
  opacity: 0.9;
  @container attachment-card (max-width: ${CARD_DROP_SIZE_MAX}) {
    display: none;
  }
`;

/* DOC-COMPLETENESS version FACT (D9 deterministic status copy, house voice —
   lowercase, calm). Rendered from DATA (`attachmentsSerialized[].docVersion`),
   never from answer text: INCIDENT-20260901 — the server used to append this
   line to the durable copy, which made it differ from the live copy of the
   same answer and defeated every duplicate-answer belt. Rendering it here
   gives every lane (task, inline SSE, VM) the identical line. */
const DocVersionFactLine = styled.p`
  margin: 0 0 0.45rem 0;
  font-size: 0.8rem;
  font-style: italic;
  line-height: 1.35;
  color: var(--att-muted, ${ATTACHMENT_COLORS.iconColor});
  opacity: 0.82;
`;

const ExportDownloadBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.5rem;
  border-radius: 6px;
  border: 1px solid var(--att-border, ${ATTACHMENT_COLORS.border});
  background: #ffffff;
  color: var(--att-ink, ${ATTACHMENT_COLORS.iconColor});
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  /* flex: none (2026-08-01): the action button can never crush the name
     column — the name ellipsizes instead. Its label never wraps either. */
  flex: none;
  white-space: nowrap;
  /* CSS backstop for the giant-glyph regression (2026-08-02): whatever SVG ends
     up in this badge renders at label scale, never at SVG default (300×150). */
  svg {
    width: 13px;
    height: 13px;
    flex: none;
  }

  &:hover {
    background: var(--att-ink, ${ATTACHMENT_COLORS.iconColor});
    color: #ffffff;
    /* AttachmentCard's descendant rule pins svg stroke to the accent — override
       both here or the glyph vanishes blue-on-blue at hover. */
    svg {
      color: #ffffff;
      stroke: #ffffff;
    }
  }

  /* Round 2 (2026-08-02): priority 2 — the action compacts before the name
     suffers. Wide cards show the full "Export / Download PDF" label; narrow
     cards show icon + the short format label ("PDF"/"DOCX"). Both labels are
     always in the DOM — the container query flips which one displays — and
     the full wording rides on the badge's aria-label/title, so nothing is
     lost at any width. */
  [data-attachment-action-label='compact'] {
    display: none;
  }

  @container attachment-card (max-width: ${CARD_COMPACT_ACTION_MAX}) {
    [data-attachment-action-label='full'] {
      display: none;
    }
    [data-attachment-action-label='compact'] {
      display: inline;
    }
  }
`;

/**
 * Decorative click-to-preview hint (2026-08-02 card polish): the whole card has
 * opened the in-platform previewer since the universal-preview round, but nothing
 * SAID so — the only visible action was Export/Download, so the cards read as
 * download-ware. Ghost pill, aria-hidden (the card's aria-label already leads with
 * "Preview attachment: …"); yields at the same width the action label compacts,
 * ahead of every element in the round-2 shrink cascade.
 */
const PreviewHintBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.5rem;
  border-radius: 6px;
  border: 1px dashed var(--att-border, ${ATTACHMENT_COLORS.border});
  background: transparent;
  color: var(--att-ink, ${ATTACHMENT_COLORS.iconColor});
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  flex: none;
  white-space: nowrap;
  opacity: 0.85;

  svg {
    width: 13px;
    height: 13px;
    flex: none;
  }

  @container attachment-card (max-width: ${CARD_COMPACT_ACTION_MAX}) {
    display: none;
  }
`;

/**
 * Cloud indicator shown when attachment is stored in Firebase Storage
 * and requires network to download (no local dataUrl available).
 */
const CloudIndicator = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex: none;
  opacity: 0.7;

  svg {
    width: 14px;
    height: 14px;
    color: var(--att-ink, ${ATTACHMENT_COLORS.iconColor});
  }

  /* Round 2 (2026-08-02): priority 4 (last) — the decorative glyph yields
     before the name ever could. The card's aria-label ("stored in cloud")
     and title ("Downloads from cloud storage") keep carrying the fact. */
  @container attachment-card (max-width: ${CARD_DROP_CLOUD_MAX}) {
    display: none;
  }
`;

/**
 * Cloud icon SVG for indicating cloud-stored attachments.
 */
const CloudIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
  </svg>
);

// ============================================================================
// Icon Components
// ============================================================================

interface AttachmentIconDisplayProps {
  category: FileCategory;
}

const AttachmentIconDisplay: React.FC<AttachmentIconDisplayProps> = ({ category }) => {
  const strokeColor = ATTACHMENT_COLORS.iconColor;
  const iconStyle = { width: '100%', height: '100%' };

  switch (category) {
    case 'image':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      );
    case 'pdf':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      );
    case 'document':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      );
    case 'spreadsheet':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      );
    case 'presentation':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <rect x="8" y="12" width="8" height="6" rx="1" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
          <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <polyline points="13 2 13 9 20 9" />
        </svg>
      );
  }
};

/**
 * Explicit width/height are LOAD-BEARING (production regression, Miles screenshots
 * 2026-08-02): an SVG with only a viewBox defaults to 300×150 CSS px, and this glyph
 * sits inside the white ExportDownloadBadge — unsized it ballooned into a giant
 * download tray that dominated every deliverable card and shoved the label outside
 * the card. jsdom computes no layout, so only the attribute form is test-pinnable
 * (previewPortalGuard.test.tsx).
 */
const DownloadIcon: React.FC = () => (
  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

/** Eye glyph for the decorative click-to-preview hint (sized: same rule as above). */
const PreviewEyeIcon: React.FC = () => (
  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Determine the attachment variant based on source tag.
 * Note: This is still used for contextual labels, but all variants
 * now use the same unified blue color scheme.
 */
export function getAttachmentVariant(sourceTag?: string): AttachmentVariant {
  if (sourceTag === 'research_result' || sourceTag === 'research_result_streaming') {
    return 'research';
  }
  if (sourceTag === 'format_result' || sourceTag === 'format_streaming') {
    return 'formatted';
  }
  return 'default';
}

/**
 * Raster image types that render as an inline thumbnail (Fix 3). Anything
 * else under image/* — SVG first of all — takes the standard attachment
 * card. 2026-09-01 production report (Miles, "butterfly.svg (attached
 * below)" rendered as an empty sliver): a generated SVG usually declares no
 * intrinsic pixel size, so the thumbnail's `width:auto; height:auto` <img>
 * collapsed to nothing and, because the fetch succeeded, the broken-image
 * placeholder never fired either. Vector deliverables now get the same
 * card + Preview + Export/Download chrome as every other file; the preview
 * overlay sizes them explicitly.
 */
const INLINE_RASTER_MIME_RX = /^image\/(png|jpe?g|gif|webp|avif|bmp|x-icon|vnd\.microsoft\.icon)$/i;
const INLINE_RASTER_EXT_RX = /\.(png|jpe?g|gif|webp|avif|bmp|ico)$/i;

/** True only for raster images that can be shown as an inline thumbnail. */
export function isInlineRasterImage(name: string | undefined, mimeType: string | undefined): boolean {
  const mime = (mimeType || '').trim();
  if (mime) {
    if (INLINE_RASTER_MIME_RX.test(mime)) return true;
    // A declared image/* mime that is NOT a known raster (svg+xml, heic,
    // tiff, photoshop…) is never inlined — the card is the honest surface.
    if (/^image\//i.test(mime)) return false;
  }
  return INLINE_RASTER_EXT_RX.test((name || '').trim());
}

function getExportFormatLabel(fileName: string, mimeType: string): string {
  const lowerName = fileName.toLowerCase();
  const lowerMime = (mimeType || '').toLowerCase();
  // Tier-21 3D scenes (2026-08-01): the card badge names the deliverable honestly.
  if (/\.(3d|scene)\.html?$/.test(lowerName)) return '3D scene';
  // Vector deliverables (2026-09-01): named explicitly rather than via the
  // generic extension fallback so the badge reads the same with or without
  // a file extension on the record.
  if (lowerName.endsWith('.svg') || lowerMime === 'image/svg+xml') return 'SVG';
  if (lowerName.endsWith('.html') || lowerName.endsWith('.htm') || lowerMime.includes('html')) return 'HTML';
  if (lowerName.endsWith('.pdf') || lowerMime.includes('pdf')) return 'PDF';
  // Inline media (2026-09-08): the badge names the container even when the record has no extension.
  if (lowerName.endsWith('.mp4') || lowerMime === 'video/mp4') return 'MP4';
  if (lowerName.endsWith('.mov') || lowerMime === 'video/quicktime') return 'MOV';
  if (lowerName.endsWith('.webm') || lowerMime === 'video/webm') return 'WEBM';
  if (lowerName.endsWith('.mp3') || lowerMime === 'audio/mpeg' || lowerMime === 'audio/mp3') return 'MP3';
  if (lowerName.endsWith('.wav') || lowerMime === 'audio/wav' || lowerMime === 'audio/x-wav') return 'WAV';
  if (lowerName.endsWith('.m4a') || lowerMime === 'audio/mp4' || lowerMime === 'audio/x-m4a') return 'M4A';
  if (lowerName.endsWith('.docx') || lowerMime.includes('wordprocessingml')) return 'DOCX';
  if (lowerName.endsWith('.doc') || lowerMime === 'application/msword') return 'DOC';
  // F3 universal cards (2026-08-03): lifted deliverables of ANY type name
  // their format honestly instead of a generic 'File' badge.
  if (lowerName.endsWith('.xlsx') || lowerMime.includes('spreadsheetml')) return 'Excel';
  if (lowerName.endsWith('.xls') || lowerMime === 'application/vnd.ms-excel') return 'Excel';
  if (lowerName.endsWith('.csv') || lowerMime === 'text/csv') return 'CSV';
  if (lowerName.endsWith('.pptx') || lowerMime.includes('presentationml')) return 'Slides';
  if (lowerName.endsWith('.zip') || lowerMime === 'application/zip') return 'ZIP';
  if (lowerName.endsWith('.md') || lowerMime.includes('markdown')) return 'Markdown';
  if (lowerName.endsWith('.txt') || lowerMime.startsWith('text/')) return 'Text';
  const extMatch = /\.([a-z0-9]{1,8})$/.exec(lowerName);
  if (extMatch && !/^html?$/.test(extMatch[1])) return extMatch[1].toUpperCase();
  return 'File';
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * ImageLightbox — full-size image preview opened by clicking an inline
 * attachment thumbnail. Renders into document.body via portal so its
 * fixed-position backdrop is not clipped by ancestor overflow:hidden
 * (the chat container clips at its rounded corners). Closes on
 * backdrop click or Escape.
 */
export const ImageLightbox: React.FC<{
  src: string;
  alt: string;
  onClose: () => void;
}> = ({ src, alt, onClose }) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const content = (
    <ImageLightboxBackdrop
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${alt} preview`}
      data-prest0n-portal="true"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <img src={src} alt={alt} onClick={(e) => e.stopPropagation()} />
      <ImageLightboxCloseButton
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="close preview"
        title="close preview"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </ImageLightboxCloseButton>
    </ImageLightboxBackdrop>
  );

  if (typeof document === 'undefined') return content;
  return ReactDOM.createPortal(content, document.body);
};

/** True when the record can be fetched AGAIN from storage — any durable
 *  locator (a signed URL, the Storage path, or the cloud-ingest gcsPath). */
export function hasDurableLocator(att: SerializedAttachment | undefined | null): boolean {
  return !!(att && (att.storageUrl || att.storagePath || att.gcsPath));
}

/**
 * How long a tile rendered from the live File keeps its 'uploading…' label
 * while the record has no durable locator (2026-09-07 photo-vanish fix: a
 * group send now waits for the FULL upload, so the sender sees the wait).
 * Bounded so a permanently failed upload falls back to today's size label
 * (the in-session heal retries at 60 s and 5 min); on reload there is no
 * File and the tile renders from the record as before.
 */
const UPLOADING_LABEL_PATIENCE_MS = 2 * 60_000;

/**
 * Inline-image recovery, rung 1 — the OWNER re-mints a fresh download URL
 * from `storagePath` (the signed storageUrl expires after 7 days; the
 * object does not). Null when the record has no path or nobody is signed
 * in; rejects when Storage denies (another participant's file — the
 * resolver rung handles them). Dynamic imports keep Firebase Storage out of
 * the bundle until an image actually breaks.
 */
async function remintAttachmentUrlViaStoragePath(
  serialized: SerializedAttachment | undefined,
): Promise<string | null> {
  const storagePath = serialized?.storagePath;
  if (!storagePath) return null;
  const { getAuth } = await import('firebase/auth');
  if (!getAuth().currentUser) return null;
  const { storage, ref, getDownloadURL } = await import('../../utils/firebaseStorage');
  return getDownloadURL(ref(storage, storagePath));
}

/**
 * Inline-image recovery, rung 2 — for EVERYONE: the authenticated server
 * resolver (the same path the previewer and the download helper use) reads
 * the object server-side and the bytes become a Blob object URL. The caller
 * owns the URL and revokes it (unmount / replacement). Null when the record
 * carries nothing the resolver can use.
 */
async function resolveAttachmentBlobUrl(
  serialized: SerializedAttachment | undefined,
  context: AttachmentResolveContext | null,
): Promise<string | null> {
  if (!serialized || !canResolveServerSide(serialized, context)) return null;
  const { bytes, contentType } = await resolveAttachmentContent(serialized, context);
  const blob = new Blob([bytes], { type: contentType || serialized.type || 'application/octet-stream' });
  return URL.createObjectURL(blob);
}

/**
 * Inline-image recovery, rung 2 — for EVERYONE, and CHEAP: the same authorized
 * server resolver answers with a short-lived signed read URL instead of the
 * file's bytes, and the browser loads it from GCS directly (2026-09-12).
 *
 * This sits ABOVE the byte rung because it is the same question with a better
 * answer: cacheable by the browser and the CDN, decoded off the main thread,
 * no 33% base64 inflation, and no Cloud Function invocation on the next view.
 * Part 4 is what made it urgent — carrying `storageUrlMintedAt` into the
 * shared record finally made `isStaleSignedUrl` fire on a RECIPIENT's device
 * at day six, which sends every render of every older photo down this ladder.
 */
async function resolveAttachmentSignedUrl(
  serialized: SerializedAttachment | undefined,
  context: AttachmentResolveContext | null,
): Promise<string | null> {
  if (!serialized || !canResolveServerSide(serialized, context)) return null;
  return resolveAttachmentDisplayUrl(serialized, context);
}

/**
 * THE RECOVERY LADDER, DECLARED ONCE (2026-09-12).
 *
 * Two tiles climb it — the inline image and the inline video/audio — and until
 * now each carried its own byte-identical copy with a hand-written `< 2` bound
 * and a hand-written "stage === 2 owns the blob" rule. Adding a rung meant
 * editing the same logic twice and keeping two magic numbers in step; this
 * repo has a recorded history of exactly that shape going wrong (three copies
 * of the file-admission code turned two alerts into six, 2026-09-11).
 *
 * So the rungs are data. The bound is the list's own length, `ownsObjectUrl`
 * says which rung's result must be revoked, and a test enumerates the list
 * rather than asserting a count it would have to be taught to update.
 */
interface AttachmentSrcRung {
  /** Stable name — the test enumerates these; not user-visible. */
  name: string;
  /** True when the rung mints an object URL the caller must revoke. */
  ownsObjectUrl: boolean;
  resolve: (
    serialized: SerializedAttachment | undefined,
    context: AttachmentResolveContext | null,
  ) => Promise<string | null>;
}

export const ATTACHMENT_SRC_LADDER: ReadonlyArray<AttachmentSrcRung> = [
  { name: 'remint-own-storage-path', ownsObjectUrl: false, resolve: (s) => remintAttachmentUrlViaStoragePath(s) },
  { name: 'resolver-signed-url', ownsObjectUrl: false, resolve: resolveAttachmentSignedUrl },
  { name: 'resolver-bytes-blob', ownsObjectUrl: true, resolve: resolveAttachmentBlobUrl },
];

const InlineAttachmentStatus = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 0.72rem;
  color: var(--brand-text-secondary, inherit);
  opacity: 0.85;
`;

/**
 * Hook: derive a stable image src for either a File or a serialized
 * attachment. For files we generate an object URL (revoked on
 * unmount); for serialized entries we prefer the durable storageUrl
 * over the (potentially huge) base64 dataUrl so re-renders don't
 * thrash data URLs into the DOM.
 *
 * Returns null when no image source is available.
 */
function useImageSrc(file: File | undefined, serialized: SerializedAttachment | undefined): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (file) {
      let url: string | null = null;
      try {
        url = URL.createObjectURL(file);
      } catch {
        setSrc(null);
        return;
      }
      setSrc(url);
      return () => {
        if (url) {
          try { URL.revokeObjectURL(url); } catch { /* ignore */ }
        }
      };
    }
    if (serialized) {
      // Durable URL preferred — survives full reload, logout/login, new sessions.
      // The display rendition wins when one exists (2026-09-12): it is the
      // copy this browser can definitely paint, and the original — which it
      // may not decode — stays the record and the download.
      const next = serialized.displayUrl || serialized.storageUrl || serialized.dataUrl || null;
      setSrc(next);
      return;
    }
    setSrc(null);
    return;
  }, [file, serialized]);
  return src;
}

/**
 * Inline image preview block — thumbnail (max 320×240, aspect preserved)
 * with click-to-lightbox. Renders a graceful "attachment unavailable"
 * placeholder ONLY after the recovery ladder is exhausted.
 *
 * Recovery ladder (2026-09-07 owner report: a photo sent to another user in
 * a shared thread vanished when coming back, and the sender's own copy went
 * blank once the 7-day signed URL expired). When the record's own src fails
 * — or the record has no renderable src at all (storagePath / gcsPath only,
 * or a locator-free record whose owning message is known) — the tile climbs:
 *   1. owner re-mint via `storagePath` (fresh download URL);
 *   2. the server resolver (everyone) → Blob object URL, revoked on unmount;
 *   3. the placeholder.
 * Backs Fix 3 for image attachments.
 */
const InlineImagePreview: React.FC<{
  /** The record's own renderable src (storageUrl / dataUrl / object URL), or null. */
  src: string | null;
  name: string;
  /** The serialized record, for the recovery ladder. Omit for live File tiles. */
  serialized?: SerializedAttachment;
  /** Owning message id — lets the resolver reconstruct the path for a locator-free record. */
  messageId?: string;
  /** Shared thread id — authorizes a PARTICIPANT's server read of a teammate's
   *  photo once the signed URL has expired (owner report 2026-09-07). */
  threadId?: string;
  /** Live upload still pending on this session's own File (see UPLOADING_LABEL_PATIENCE_MS). */
  uploading?: boolean;
}> = ({ src, name, serialized, messageId, threadId, uploading = false }) => {
  const [recoveredSrc, setRecoveredSrc] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  // Index into ATTACHMENT_SRC_LADDER: how many rungs have been tried.
  const stageRef = useRef(0);
  // Bumped on every reset so an in-flight climb from a previous record is abandoned.
  const generationRef = useRef(0);
  const inFlightRef = useRef(false);
  const blobUrlRef = useRef<string | null>(null);
  const aliveRef = useRef(true);
  // Stable identity: a fresh object every render would re-run the reset effect forever.
  const resolveContext = useMemo<AttachmentResolveContext | null>(
    () => (messageId ? { messageId, threadId } : null),
    [messageId, threadId],
  );

  const releaseBlob = useCallback(() => {
    if (blobUrlRef.current) {
      try { URL.revokeObjectURL(blobUrlRef.current); } catch { /* ignore */ }
      blobUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      releaseBlob();
    };
  }, [releaseBlob]);

  const recoverNext = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const generation = generationRef.current;
    try {
      while (stageRef.current < ATTACHMENT_SRC_LADDER.length) {
        const rung = ATTACHMENT_SRC_LADDER[stageRef.current];
        stageRef.current += 1;
        try {
          const candidate = await rung.resolve(serialized, resolveContext);
          if (!candidate) continue;
          if (!aliveRef.current || generation !== generationRef.current) {
            // Unmounted or the record changed under us — never leak the blob.
            if (rung.ownsObjectUrl) { try { URL.revokeObjectURL(candidate); } catch { /* ignore */ } }
            return;
          }
          if (rung.ownsObjectUrl) {
            releaseBlob();
            blobUrlRef.current = candidate;
          }
          setRecoveredSrc(candidate);
          return;
        } catch {
          // this rung failed — climb to the next
        }
      }
      if (aliveRef.current && generation === generationRef.current) setBroken(true);
    } finally {
      inFlightRef.current = false;
    }
  }, [serialized, resolveContext, releaseBlob]);

  // A new record (locators upgraded / healed) restarts the ladder; a record
  // with no renderable src of its own starts climbing at once. A signed URL
  // past its safe age (PR-6, 2026-09-09: SIGNED_URL_STALE_MS, six of its seven
  // days) re-mints proactively — the stale src keeps showing until the fresh
  // one lands, so nothing flickers and nothing goes blank on day 8.
  useEffect(() => {
    generationRef.current += 1;
    stageRef.current = 0;
    setBroken(false);
    setRecoveredSrc(null);
    releaseBlob();
    if (!src || isStaleSignedUrl(serialized)) void recoverNext();
  }, [src, serialized, resolveContext, recoverNext, releaseBlob]);

  const effectiveSrc = recoveredSrc ?? src;

  /**
   * WHAT A RECIPIENT PAINTS WHILE THE BYTES ARE STILL COMING (2026-09-11).
   *
   * Malaika: "everyone should see the message and the image show up at the
   * exact same time … so as not to cause confusion even when on opposite
   * devices." That was impossible with what the record carried. A recipient
   * can never read the bytes directly — storage.rules is owner-only — so the
   * tile climbs a ladder that ends in a Cloud Function round trip, and until
   * it lands there was nothing to draw and no idea how big to draw it.
   *
   * Two facts on the record change that. The dimensions reserve the true box
   * BEFORE anything arrives, so the conversation stops jumping when a picture
   * lands; the sub-kilobyte preview fills that box immediately, blurred,
   * and is replaced the moment the real image decodes.
   *
   * Both are absent on every record written before today, and absent means
   * exactly the previous behaviour — the text placeholder below.
   */
  const previewSrc = typeof serialized?.preview === 'string' && serialized.preview.startsWith('data:image/')
    ? serialized.preview
    : undefined;
  const boxStyle: React.CSSProperties | undefined =
    typeof serialized?.width === 'number' && serialized.width > 0
      && typeof serialized?.height === 'number' && serialized.height > 0
      ? { aspectRatio: `${serialized.width} / ${serialized.height}` }
      : undefined;

  if (broken) {
    return (
      <InlineAttachmentBrokenPlaceholder role="status" data-attachment-unavailable="">
        <span>attachment unavailable: {name}</span>
      </InlineAttachmentBrokenPlaceholder>
    );
  }
  if (!effectiveSrc) {
    // The picture itself, at a twenty-fourth of its size, while the real one
    // travels. Blurred rather than sharpened: it IS low-resolution, and
    // pretending otherwise reads as a broken image instead of a loading one.
    if (previewSrc) {
      return (
        <InlineAttachmentImage as="div" data-attachment-preview="" style={boxStyle}>
          <img
            src={previewSrc}
            alt={name}
            aria-busy="true"
            data-attachment-preview-image=""
            style={{ filter: 'blur(8px)', transform: 'scale(1.04)', width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </InlineAttachmentImage>
      );
    }
    // Climbing the ladder with nothing to show yet — never the red placeholder.
    return (
      <InlineAttachmentStatus role="status" data-attachment-recovering="">
        loading {name}…
      </InlineAttachmentStatus>
    );
  }
  const renderImage = () => {
    return (
      <ImageHoverWrapper src={effectiveSrc} alt={name}>
        <InlineAttachmentImage
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowLightbox(true);
          }}
          aria-label={`open ${name}`}
          title={name}
          style={boxStyle}
        >
          <img
            src={effectiveSrc}
            alt={name}
            loading="lazy"
            decoding="async"
            data-attachment-inline-image=""
            // The placeholder sits BEHIND the real image rather than being
            // swapped out: a transparent PNG or a slow decode then shows the
            // picture rather than the page's background, and there is no
            // frame where the box is empty.
            style={previewSrc
              ? { backgroundImage: `url("${previewSrc}")`, backgroundSize: 'cover', backgroundPosition: 'center' }
              : undefined}
            onError={() => { void recoverNext(); }}
          />
        </InlineAttachmentImage>
      </ImageHoverWrapper>
    );
  };

  return (
    <>
      {renderImage()}
      {uploading && (
        <InlineAttachmentStatus role="status" data-attachment-uploading="">
          uploading…
        </InlineAttachmentStatus>
      )}
      {showLightbox && (
        <ImageLightbox
          src={effectiveSrc}
          alt={name}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </>
  );
};

/**
 * Inline video / audio in the bubble (2026-09-08, Miles: "the default when attaching a video,
 * like the picture, should be the user can preview play within the chat interface, also open up
 * larger in preview window, also download").
 *
 * The same recovery ladder as the image tile (own src → owner re-mint via storagePath → server
 * resolver blob → placeholder), a real <video>/<audio> with native controls, and a meta row with
 * the name, the size, "open larger" (the platform previewer, body-portaled) and the same
 * Export / Download badge the card shows. Memoized on its primitive props: the chat list re-renders
 * on every streamed token, and a playing element must never be rebuilt under the user.
 *
 * Playback hygiene: `preload="metadata"` fetches only the index (the moov box the platform now
 * moves first), `playsInline` keeps iOS Safari from hijacking the page, and while a clip plays the
 * pane's backdrop blur is lifted (`html[data-prest0n-media-playing]` — styles.ts ChatContainer).
 */
const MEDIA_PLAYING_ATTR = 'data-prest0n-media-playing';
let mediaPlayingCount = 0;
function noteMediaPlaying(delta: 1 | -1): void {
  if (typeof document === 'undefined') return;
  mediaPlayingCount = Math.max(0, mediaPlayingCount + delta);
  if (mediaPlayingCount > 0) document.documentElement.setAttribute(MEDIA_PLAYING_ATTR, '1');
  else document.documentElement.removeAttribute(MEDIA_PLAYING_ATTR);
}

const InlineMediaPreview: React.FC<{
  kind: 'video' | 'audio';
  /** The record's own renderable src (storageUrl / object URL / dataUrl), or null. */
  src: string | null;
  name: string;
  size: number;
  exportFormat: string;
  /** The serialized record, for the recovery ladder. Omit for live File tiles. */
  serialized?: SerializedAttachment;
  messageId?: string;
  threadId?: string;
  uploading?: boolean;
  canDownload: boolean;
  onOpenLarger: () => void;
  onDownload: () => void;
}> = React.memo(({ kind, src, name, size, exportFormat, serialized, messageId, threadId, uploading = false, canDownload, onOpenLarger, onDownload }) => {
  const [recoveredSrc, setRecoveredSrc] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  const stageRef = useRef(0);
  const generationRef = useRef(0);
  const inFlightRef = useRef(false);
  const blobUrlRef = useRef<string | null>(null);
  const aliveRef = useRef(true);
  const playingRef = useRef(false);
  const resolveContext = useMemo<AttachmentResolveContext | null>(
    () => (messageId ? { messageId, threadId } : null),
    [messageId, threadId],
  );

  const releaseBlob = useCallback(() => {
    if (blobUrlRef.current) {
      try { URL.revokeObjectURL(blobUrlRef.current); } catch { /* ignore */ }
      blobUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      releaseBlob();
      if (playingRef.current) { playingRef.current = false; noteMediaPlaying(-1); }
    };
  }, [releaseBlob]);

  const recoverNext = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const generation = generationRef.current;
    try {
      while (stageRef.current < ATTACHMENT_SRC_LADDER.length) {
        const rung = ATTACHMENT_SRC_LADDER[stageRef.current];
        stageRef.current += 1;
        try {
          const candidate = await rung.resolve(serialized, resolveContext);
          if (!candidate) continue;
          if (!aliveRef.current || generation !== generationRef.current) {
            if (rung.ownsObjectUrl) { try { URL.revokeObjectURL(candidate); } catch { /* ignore */ } }
            return;
          }
          if (rung.ownsObjectUrl) {
            releaseBlob();
            blobUrlRef.current = candidate;
          }
          setRecoveredSrc(candidate);
          return;
        } catch {
          // this rung failed — climb to the next
        }
      }
      if (aliveRef.current && generation === generationRef.current) setBroken(true);
    } finally {
      inFlightRef.current = false;
    }
  }, [serialized, resolveContext, releaseBlob]);

  useEffect(() => {
    generationRef.current += 1;
    stageRef.current = 0;
    setBroken(false);
    setRecoveredSrc(null);
    releaseBlob();
    if (!src) void recoverNext();
  }, [src, serialized, resolveContext, recoverNext, releaseBlob]);

  const onPlay = useCallback(() => {
    if (!playingRef.current) { playingRef.current = true; noteMediaPlaying(1); }
  }, []);
  const onStop = useCallback(() => {
    if (playingRef.current) { playingRef.current = false; noteMediaPlaying(-1); }
  }, []);

  const effectiveSrc = recoveredSrc ?? src;
  if (broken) {
    return (
      <InlineAttachmentBrokenPlaceholder role="status" data-attachment-unavailable="">
        <span>attachment unavailable: {name}</span>
      </InlineAttachmentBrokenPlaceholder>
    );
  }
  if (!effectiveSrc) {
    return (
      <InlineAttachmentStatus role="status" data-attachment-recovering="">
        loading {name}…
      </InlineAttachmentStatus>
    );
  }
  const stop = (e: React.SyntheticEvent) => { e.stopPropagation(); };
  return (
    <div data-attachment-inline-media={kind} onClick={stop} onKeyDown={stop} role="presentation">
      <InlineMediaFrame>
        {kind === 'video' ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            src={effectiveSrc}
            controls
            playsInline
            preload="metadata"
            title={name}
            data-attachment-inline-video=""
            onPlay={onPlay}
            onPause={onStop}
            onEnded={onStop}
            onError={() => { void recoverNext(); }}
          />
        ) : (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio
            src={effectiveSrc}
            controls
            preload="metadata"
            title={name}
            data-attachment-inline-audio=""
            onPlay={onPlay}
            onPause={onStop}
            onEnded={onStop}
            onError={() => { void recoverNext(); }}
          />
        )}
      </InlineMediaFrame>
      <InlineMediaMetaRow>
        <span data-inline-media-name="" title={name}>{truncateFilename(name, 35)}</span>
        <span data-inline-media-size="">{uploading ? 'uploading…' : size > 0 ? formatFileSize(size) : ''}</span>
        <InlineMediaActionButton
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenLarger(); }}
          aria-label={`Open ${name} larger`}
          title="open larger"
          data-inline-media-open-larger=""
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
          <span>open larger</span>
        </InlineMediaActionButton>
        {canDownload && (
          <InlineMediaActionButton
            type="button"
            onClick={(e) => { e.stopPropagation(); onDownload(); }}
            aria-label={`Export / Download ${exportFormat}`}
            title={`Export / Download ${exportFormat}`}
            data-inline-media-download=""
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            <span>{`Export / Download ${exportFormat}`}</span>
          </InlineMediaActionButton>
        )}
      </InlineMediaMetaRow>
    </div>
  );
}, (prev, next) => (
  prev.kind === next.kind
  && prev.src === next.src
  && prev.name === next.name
  && prev.size === next.size
  && prev.exportFormat === next.exportFormat
  && prev.messageId === next.messageId
  && prev.threadId === next.threadId
  && prev.uploading === next.uploading
  && prev.canDownload === next.canDownload
  && prev.serialized?.storageUrl === next.serialized?.storageUrl
  && prev.serialized?.storagePath === next.serialized?.storagePath
  && prev.serialized?.gcsPath === next.serialized?.gcsPath
  && prev.serialized?.dataUrl === next.serialized?.dataUrl
));
InlineMediaPreview.displayName = 'InlineMediaPreview';

/**
 * ChatAttachment - Unified attachment display component.
 * 
 * Handles both File objects and SerializedAttachment with consistent
 * styling and behavior. Uses a unified blue color scheme for visual
 * consistency across all attachment contexts (user uploads, research
 * reports, formatted reports).
 *
 * Fix 3 (2026-05-10): when the attachment is an image AND a usable
 * source is available, render an inline thumbnail with click-to-
 * lightbox; otherwise fall back to the existing icon + filename tile
 * (clickable to download). The durable URL on the message record is
 * the primary source for cross-session persistence; transient blob
 * URLs only apply within the originating session.
 */
export const ChatAttachment: React.FC<ChatAttachmentProps & {
  /** The serialized sibling of a live `file` tile (same send, same name) —
   *  drives the 'uploading…' label until it gains a durable locator
   *  (2026-09-07 photo-vanish fix). Never a render source. */
  uploadRecord?: SerializedAttachment;
}> = ({
  file,
  serialized,
  animate = true,
  messageId,
  threadId,
  threadTitle,
  onUserBubble = false,
  uploadRecord,
}) => {
  // Derive metadata from either File or SerializedAttachment
  const name = useMemo(() => file?.name || serialized?.name || 'attachment', [file, serialized]);
  const type = useMemo(() => file?.type || serialized?.type || '', [file, serialized]);
  const size = useMemo(() => file?.size || serialized?.size || 0, [file, serialized]);
  const dataUrl = serialized?.dataUrl;
  const storageUrl = serialized?.storageUrl;
  const storagePath = serialized?.storagePath;

  // Server resolver reach (2026-08-20 attachment-durability round): a
  // metadata-only record (name, no locator) is RECOVERABLE when the owning
  // messageId is known — prest0nResolveAttachmentContent reconstructs the
  // storage path from name+messageId. Before this, such records rendered as
  // dead "sync needed" shells even though one authenticated call away.
  const canResolveViaServer = canResolveServerSide(
    serialized ?? null,
    messageId ? { messageId, threadId } : null,
  );

  // Determine if attachment is downloadable
  // Priority: File object > Firebase Storage URL > storagePath (on-demand URL) > base64 dataUrl > server resolver
  const canDownload = !!(file || storageUrl || storagePath || dataUrl || (canResolveViaServer && serialized));

  // Determine if attachment relies only on cloud storage (no local data)
  // This indicates cross-device restore where attachment requires network to download
  const isCloudOnly = !file && !dataUrl && !!(storageUrl || storagePath);

  // Get file category for icon
  const category = useMemo(() => getFileCategory(type), [type]);

  // Image source for inline thumbnail (Fix 3) — must run unconditionally
  // so the hook order is stable across re-renders.
  const imageSrc = useImageSrc(file, serialized);

  // Live upload state (2026-09-07 photo-vanish fix): a tile rendered from
  // this session's own File whose record has no durable locator yet is still
  // uploading — a group send waits for the full upload, so the wait is shown
  // honestly ('uploading…') instead of a size that looks final. Bounded by
  // UPLOADING_LABEL_PATIENCE_MS so a dead upload never pins the label.
  const uploadPendingRaw = !!file && !!uploadRecord && !hasDurableLocator(uploadRecord);
  const [uploadLabelExpired, setUploadLabelExpired] = useState(false);
  useEffect(() => {
    if (!uploadPendingRaw) return undefined;
    const timer = setTimeout(() => setUploadLabelExpired(true), UPLOADING_LABEL_PATIENCE_MS);
    return () => clearTimeout(timer);
  }, [uploadPendingRaw]);
  const uploadPending = uploadPendingRaw && !uploadLabelExpired;

  /**
   * How far along, when the lane can say (2026-09-11).
   *
   * `uploading…` alone cannot distinguish a slow upload from a dead one for
   * two whole minutes, which on a phone is the difference between waiting
   * and re-attaching. The resumable SDK lane reports bytes transferred;
   * polled rather than subscribed because the reporter is a module-level
   * WeakMap keyed by File and this component already holds the File — one
   * interval, only while an upload is actually pending, and gone the moment
   * it is not. Lanes that cannot report leave this undefined and the label
   * is exactly what it was.
   */
  const [uploadPercent, setUploadPercent] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (!uploadPending || !file) {
      setUploadPercent(undefined);
      return undefined;
    }
    const read = () => {
      const p = getUploadProgress(file);
      setUploadPercent(typeof p === 'number' ? Math.min(99, Math.floor(p * 100)) : undefined);
    };
    read();
    const t = setInterval(read, 500);
    return () => clearInterval(t);
  }, [uploadPending, file]);
  const uploadingLabel = typeof uploadPercent === 'number'
    ? `uploading ${uploadPercent}%`
    : 'uploading…';

  // Whether the attachment has metadata but no usable download source
  // These are "broken" entries where Firestore saved the attachment name/type/size
  // but the storageUrl/storagePath/dataUrl were not persisted (race condition during streaming)
  const isBrokenReference = !canDownload && !!(serialized?.name);

  /**
   * Helper: validate Firebase Auth state and force token refresh before Storage access.
   * Returns true if auth is valid, false if user needs to re-authenticate.
   */
  const ensureAuthForStorage = useCallback(async (): Promise<boolean> => {
    try {
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      if (!auth.currentUser) {
        console.warn('[ChatAttachment] No authenticated user - cannot access Firebase Storage');
        alert('Your session has expired. Please sign in again to download attachments.');
        return false;
      }
      // Force token refresh to ensure we have a valid token for Storage access
      await auth.currentUser.getIdToken(true);
      return true;
    } catch (error) {
      console.error('[ChatAttachment] Auth validation failed:', error);
      alert('Authentication error. Please sign in again to download attachments.');
      return false;
    }
  }, []);

  /**
   * Helper: fetch a fresh download URL from Firebase Storage using storagePath.
   * Uses dynamic import to avoid bundling Firebase Storage code until needed.
   * Validates auth state before attempting Storage access.
   */
  const downloadViaStoragePath = useCallback(async (path: string, fileName: string) => {
    // Validate auth before accessing Firebase Storage (requires request.auth.uid == uid)
    const isAuthed = await ensureAuthForStorage();
    if (!isAuthed) return;

    const { storage, ref, getDownloadURL } = await import('../../utils/firebaseStorage');
    const freshUrl = await getDownloadURL(ref(storage, path));
    console.log('[ChatAttachment] Fresh URL from storagePath:', fileName, freshUrl.substring(0, 80));
    // R17: bytes-first save — a bare <a download> on a cross-origin URL is
    // ignored by browsers, which NAVIGATE renderable types (HTML!) instead
    // of saving them. firebasestorage.googleapis.com serves CORS, so the
    // fetch→Blob path works; the helper handles resolver/legacy fallbacks.
    await downloadAttachmentAsFile({
      url: freshUrl,
      fileName,
      declaredType: type,
      serialized,
      context: messageId ? { messageId, threadId } : null,
    });
  }, [ensureAuthForStorage, type, serialized, messageId, threadId]);

  // Handle click to download
  // Priority cascade: File > storageUrl > storagePath (on-demand refresh) > dataUrl
  const handleClick = useCallback(async () => {
    if (!canDownload) {
      console.error('[ChatAttachment] Download blocked - no download source', {
        name,
        hasFile: !!file,
        hasStorageUrl: !!storageUrl,
        hasStoragePath: !!storagePath,
        hasDataUrl: !!dataUrl,
        size,
      });
      return;
    }

    // Log which download path is being used
    if (file) {
      console.log('[ChatAttachment] Downloading from File object:', name, 'size:', file.size);
      // Priority 1: Runtime File object (current session)
      const fileUrl = URL.createObjectURL(file);
      const anchor = document.createElement('a');
      anchor.href = fileUrl;
      anchor.download = name;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.click();
      // Cleanup
      setTimeout(() => URL.revokeObjectURL(fileUrl), 0);
    } else if (storageUrl) {
      console.log('[ChatAttachment] Downloading from Firebase Storage:', name, storageUrl.substring(0, 80));
      // Priority 2: Firebase Storage URL (cross-device persistence).
      // R17 (2026-08-14 "I can't even download it"): the old path HEAD-checked
      // then set a.download on the CROSS-ORIGIN URL — browsers ignore the
      // attribute cross-origin and NAVIGATE renderable types (HTML!) instead
      // of saving. Bytes-first now: fetch → Blob save, authenticated server
      // resolver when the signed URL blocks CORS, storagePath refresh last.
      try {
        const saved = await downloadAttachmentAsFile({
          url: storageUrl,
          fileName: name,
          declaredType: type,
          serialized,
          context: messageId ? { messageId, threadId } : null,
        });
        if (!saved && storagePath) {
          await downloadViaStoragePath(storagePath, name);
        }
      } catch (error) {
        console.warn('[ChatAttachment] bytes-first download failed, attempting storagePath refresh:', error);
        if (storagePath) {
          try {
            await downloadViaStoragePath(storagePath, name);
          } catch (refreshError) {
            console.error('[ChatAttachment] storagePath refresh also failed:', refreshError);
            alert(`Failed to download ${name}. The file may have been deleted.`);
          }
        } else {
          alert(`Failed to download ${name}. The file may have been deleted.`);
        }
      }
    } else if (storagePath) {
      // Priority 2.5: storagePath fallback (storageUrl missing but path exists in Firebase Storage)
      console.log('[ChatAttachment] No storageUrl, fetching fresh URL from storagePath:', storagePath);
      try {
        await downloadViaStoragePath(storagePath, name);
      } catch (error) {
        console.error('[ChatAttachment] storagePath download failed:', error);
        alert(`Failed to download ${name}. Please sign in again or the file may have been deleted.`);
      }
    } else if (dataUrl) {
      console.log('[ChatAttachment] Downloading from dataUrl:', name);
      // Priority 3: Base64 dataUrl (localStorage fallback). R17: converted to
      // a Blob save — Chrome blocks top-frame data: navigation, so the old
      // anchor with target=_blank could silently no-op.
      try {
        const resp = await fetch(dataUrl);
        saveBlobAs(await resp.blob(), name);
      } catch (error) {
        console.error('[ChatAttachment] dataUrl download failed:', error);
      }
    } else if (canResolveViaServer && serialized) {
      // Priority 4 (2026-08-20): locator-free record — the authenticated
      // server resolver reconstructs the bytes from name + messageId.
      try {
        const saved = await downloadAttachmentAsFile({
          url: null,
          fileName: name,
          declaredType: type,
          serialized,
          context: messageId ? { messageId, threadId } : null,
        });
        if (!saved) {
          alert(`Failed to download ${name}. The file may have been deleted.`);
        }
      } catch (error) {
        console.error('[ChatAttachment] server-resolver download failed:', error);
        alert(`Failed to download ${name}. The file may have been deleted.`);
      }
    }
  }, [file, storageUrl, storagePath, dataUrl, name, type, size, canDownload, canResolveViaServer, serialized, messageId, threadId, downloadViaStoragePath]);

  // Format display values
  const displayName = truncateFilename(name, 35);
  const displaySize = size > 0 ? formatFileSize(size) : 'file';
  const exportFormat = getExportFormatLabel(name, type);

  // Universal preview (2026-08-01; amended 2026-08-02 directive): clicking ANY
  // attachment — every file type, every time — opens the in-platform previewer; for
  // opaque binaries (docx/xlsx/zip) the previewer shows the download card with full
  // options INSIDE it. The Export/Download badge keeps the one-click direct-download
  // path for every type.
  const canPreview = canDownload && !isBrokenReference;
  const [showPreview, setShowPreview] = useState(false);
  // The platform previewer (body-portaled) — the card's click and the inline player's
  // "open larger" both open it, so it is built once here.
  const previewOverlay = showPreview ? (
    <FilePreviewOverlay
      file={file || null}
      serialized={serialized || null}
      /* Origin (2026-08-24): messageId for the server resolver, threadId so
         the preview's "open chat" returns to this exact conversation. */
      context={(messageId || threadId) ? { messageId, threadId, threadTitle } : null}
      onClose={() => setShowPreview(false)}
    />
  ) : null;

  // ──────────────────────────────────────────────────────────────────
  // Fix 3: inline image thumbnail branch.
  //
  // When the attachment is an image AND we have a usable source
  // (live File object, durable storage URL, or persisted dataUrl),
  // render an inline thumbnail with click-to-lightbox instead of
  // the generic icon-tile. Keeps the document tile path intact for
  // PDFs / DOCX / etc. The durable storageUrl lives on the message
  // record so the preview rehydrates correctly across reload,
  // logout/login, and new sessions.
  // ──────────────────────────────────────────────────────────────────
  // Raster only (2026-09-01): SVG and any other non-raster image/* type
  // falls through to the standard card — see isInlineRasterImage.
  // A picture whose own format this browser may not decode is still inlined
  // when the sender's device left a displayable copy beside it (2026-09-12).
  // isInlineRasterImage stays a pure name+type judgement — it cannot know
  // what artifacts a record carries — so the record's own evidence is read
  // here rather than by widening that function to admit HEIC for everyone.
  const hasDisplayRendition = !!(serialized?.displayUrl || serialized?.displayPath);
  const inlineRaster = isInlineRasterImage(name, type) || hasDisplayRendition;
  // Recoverable without a renderable src (2026-09-07 photo-vanish fix): a
  // record that carries only storagePath / gcsPath — or nothing but a name
  // under a known message — still takes the inline branch, which climbs the
  // recovery ladder (owner re-mint → server resolver) before any placeholder.
  const inlineRecoverable = !file
    && !!serialized
    && !serialized.storageUrl
    && !serialized.dataUrl
    && (!!serialized.storagePath || !!serialized.gcsPath || canResolveViaServer);
  // Inline VIDEO / AUDIO (2026-09-08): the same kind classifier the previewer uses (lockstep —
  // whatever the previewer would play, the bubble plays), the same recovery ladder as pictures.
  const mediaKind = resolvePreviewKind(name, type);
  if ((mediaKind === 'video' || mediaKind === 'audio') && !isBrokenReference && (imageSrc || inlineRecoverable)) {
    return (
      <>
        <InlineMediaPreview
          kind={mediaKind}
          src={imageSrc}
          name={name}
          size={size}
          exportFormat={exportFormat}
          serialized={file ? undefined : serialized}
          messageId={messageId}
          threadId={threadId}
          uploading={uploadPending}
          canDownload={canDownload}
          onOpenLarger={() => setShowPreview(true)}
          onDownload={() => { void handleClick(); }}
        />
        {previewOverlay}
      </>
    );
  }
  if (category === 'image' && inlineRaster && !isBrokenReference && (imageSrc || inlineRecoverable)) {
    return (
      <InlineImagePreview
        src={imageSrc}
        name={name}
        serialized={file ? undefined : serialized}
        messageId={messageId}
        threadId={threadId}
        uploading={uploadPending}
      />
    );
  }
  if (isBrokenReference && inlineRaster) {
    return (
      <InlineAttachmentBrokenPlaceholder role="status">
        <span>attachment unavailable: {name}</span>
      </InlineAttachmentBrokenPlaceholder>
    );
  }

  return (
    <>
    <AttachmentCard
      $animate={animate}
      $disabled={!canDownload}
      $onGold={onUserBubble}
      onClick={canPreview ? () => setShowPreview(true) : (canDownload ? handleClick : undefined)}
      role="button"
      aria-label={
        isBrokenReference
          ? `Attachment unavailable: ${name} (sync needed)`
          : `${canPreview ? 'Preview' : canDownload ? 'Export / Download' : 'View'} attachment: ${name}${isCloudOnly ? ' (stored in cloud)' : ''}`
      }
      title={
        isBrokenReference
          ? `${name}\n(Attachment reference lost - re-run report to restore)`
          : isCloudOnly
            ? `${name}\n(Downloads from cloud storage)`
            : name
      }
      disabled={!canDownload}
    >
      <IconWrapper>
        <AttachmentIconDisplay category={category} />
      </IconWrapper>
      <MetaWrapper>
        <AttachmentFileName>{displayName}</AttachmentFileName>
        <AttachmentFileSize
          data-attachment-size=""
          data-attachment-uploading={uploadPending ? '' : undefined}
        >
          {isBrokenReference ? 'sync needed' : uploadPending ? uploadingLabel : displaySize}
        </AttachmentFileSize>
        {typeof serialized?.docVersion === 'number' && serialized.docVersion >= 2 && (
          <DocVersionChip
            data-attachment-doc-version=""
            title="an updated version of this document — it replaces the earlier one in this thread"
          >
            {`v${serialized.docVersion} · updated`}
          </DocVersionChip>
        )}
      </MetaWrapper>
      {isCloudOnly && (
        <CloudIndicator data-attachment-cloud="" title="Stored in cloud - requires internet to download">
          <CloudIcon />
        </CloudIndicator>
      )}
      {canPreview && (
        <PreviewHintBadge aria-hidden="true" data-attachment-preview-hint="">
          <PreviewEyeIcon />
          <span>Preview</span>
        </PreviewHintBadge>
      )}
      {canDownload && (
        <ExportDownloadBadge
          role="button"
          /* Round 2 (2026-08-02): at narrow card widths the visible label
             compacts to the bare format ("PDF"), so the FULL action wording
             lives on aria-label/title — identical announcement and hover
             hint at every width. */
          aria-label={`Export / Download ${exportFormat}`}
          title={`Export / Download ${exportFormat}`}
          onClick={(e: React.MouseEvent) => {
            // Badge stays the direct-download path even when the card opens a preview.
            e.stopPropagation();
            handleClick();
          }}
        >
          <DownloadIcon />
          <span data-attachment-action-label="full">{`Export / Download ${exportFormat}`}</span>
          <span data-attachment-action-label="compact">{exportFormat}</span>
        </ExportDownloadBadge>
      )}
    </AttachmentCard>
    {/* Portal modal mounted OUTSIDE the card: React portals bubble synthetic events
        through the REACT tree, so inside the button a backdrop-close click would
        re-trigger the card's open handler and reopen the overlay. */}
    {previewOverlay}
    </>
  );
};

// ============================================================================
// AttachmentsSection Component
// ============================================================================

const SectionContainer = styled.div<{ $onGold?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  /* The divider sits directly on the bubble (outside the card), so it takes
     the ink-tint variant on the gold user bubble (2026-08-20 contrast fix). */
  border-top: 1px solid ${props => props.$onGold
    ? USER_BUBBLE_ATTACHMENT_COLORS.divider
    : ATTACHMENT_COLORS.divider};
  /* Docked crush fix (2026-08-01): the section — and therefore each card —
     spans the full available message width in every mode. */
  width: 100%;
  min-width: 0;
`;

interface AttachmentsSectionProps {
  sourceTag?: string;
  attachments?: File[];
  attachmentsSerialized?: SerializedAttachment[];
  isUser?: boolean;
  /** The owning message's text — lets the version-fact line (a) mirror the
   *  answer's language and (b) stay silent on legacy rows that already carry
   *  the server-appended line (INCIDENT-20260901). */
  messageText?: string;
  /** Owning message id, forwarded to each tile for the preview resolver's
   *  locator-free context lookup (2026-08-09 preview fix). */
  messageId?: string;
  /** Owning thread id + title (2026-08-24), forwarded to each tile so a
   *  preview knows the conversation to reopen beside itself. */
  threadId?: string;
  threadTitle?: string;
}

/**
 * AttachmentsSection - Wrapper for rendering attachments with optional label.
 * 
 * IMPORTANT: Serialized attachments are only rendered as a FALLBACK when no
 * hydrated File objects are present. This prevents duplicate rendering since
 * messages often have both File objects (for immediate use) and serialized
 * data (for persistence/restoration).
 * 
 * All attachments use the unified blue color scheme regardless of their
 * source (user uploads, research reports, formatted reports).
 */
/**
 * Predicate: returns true only when `value` is a real, usable File /
 * Blob (or a duck-typed equivalent in the unit-test env). After a
 * thread-snapshot round-trips through `JSON.stringify`, every `File`
 * becomes `{}` — truthy length, no usable shape — and the renderer
 * used to follow the dead-shell branch and produce empty bubbles. We
 * gate `hasFiles` on this predicate so dead shells fall through to
 * the `attachmentsSerialized` fallback (Miles 2026-05-11, thread
 * `C0AM8U804KZ/1778281880.499349`).
 *
 * Kept inline (not imported from `threadPersist`) so this module has
 * no upstream dependency on the persistence layer.
 */
function isRealFileLike(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  if (typeof File !== 'undefined' && value instanceof File) return true;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
  const v = value as { name?: unknown; size?: unknown };
  return typeof v.name === 'string'
    && v.name.length > 0
    && typeof v.size === 'number'
    && Number.isFinite(v.size);
}

/** The serialized sibling of a live File in the same send — by name first
 *  (the send-time skeleton mirrors the File list), position as fallback. */
function uploadRecordFor(
  list: SerializedAttachment[] | undefined,
  file: File,
  index: number,
): SerializedAttachment | undefined {
  if (!Array.isArray(list) || list.length === 0) return undefined;
  return list.find((a) => a?.name === file.name) ?? list[index];
}

export const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({
  attachments,
  attachmentsSerialized,
  isUser,
  messageId,
  messageText,
  threadId,
  threadTitle,
}) => {
  // Only treat the `attachments` slot as renderable when at least one
  // entry is a real File / Blob. Empty `[{}, {}]` shells produced by
  // `JSON.stringify(File)` round-trips must NOT shadow the durable
  // `attachmentsSerialized` fallback path.
  const hasFiles = Array.isArray(attachments)
    && attachments.length > 0
    && attachments.some(isRealFileLike);
  // Only use serialized attachments as fallback when no File objects exist
  const hasSerializedOnly = !hasFiles && attachmentsSerialized && attachmentsSerialized.length > 0;

  if (!hasFiles && !hasSerializedOnly) {
    return null;
  }

  const versionFact = docVersionFactFor(attachmentsSerialized, isUser, messageText);

  return (
    <SectionContainer $onGold={isUser === true}>
      {versionFact && (
        <DocVersionFactLine data-doc-version-fact="">{versionFact}</DocVersionFactLine>
      )}
      {/* Render hydrated File objects (preferred). Filter to only
          real File / Blob instances so any post-rehydrate dead shells
          mixed in with live Files don't render as empty rows. */}
      {hasFiles && attachments!
        .filter((file): file is File => isRealFileLike(file))
        .map((file, index) => (
          <ChatAttachment
            key={`file-${index}`}
            file={file}
            uploadRecord={uploadRecordFor(attachmentsSerialized, file, index)}
            animate={true}
            messageId={messageId}
            threadId={threadId}
            threadTitle={threadTitle}
            onUserBubble={isUser === true}
          />
        ))}
      {/* Fallback: Render serialized attachments only if no File objects */}
      {hasSerializedOnly && attachmentsSerialized!.map((att, index) => (
        <ChatAttachment
          key={`serialized-${index}`}
          serialized={att}
          animate={true}
          messageId={messageId}
          threadId={threadId}
          threadTitle={threadTitle}
          onUserBubble={isUser === true}
        />
      ))}
    </SectionContainer>
  );
};

export default ChatAttachment;
